/**
 * The rows only master knows of the section „Startseite" (I-3769): one switch
 * per account whose mails can count on the home page's mail tile,
 *
 *   shell → home.unread.mail_accounts.<account uuid>   default: on
 *
 * Declared when the shell collects the declarations, like the notification
 * rows (`notificationSettings.ts`). One account alone gets no row: there is
 * nothing to choose. An account's name is no key of a table, so the rows carry
 * reference keys `homeUnreadAccountTranslator` resolves when a text is read.
 */
import type { ConnectSettingsSection } from '@blpde/connect';
import type { SharedAccount } from '@blpde/connect/auth';
import { fetchWithAuth, getApiOrigin } from '@blpde/connect/auth';
import type { Translator } from '@/i18n/appI18n';
import { HOME_SECTION_ID, SHELL_CONFIG_KEY } from './connectSettingsSections';

const T = 'connect.settingsList.fields.homeUnreadMailAccount';
/** The head of a reference key: no i18n key starts like this. */
const REF = '@homeUnreadAccount/';

/** The accounts that have mail for this session; none when master cannot tell. */
export async function fetchHomeUnreadAccounts(): Promise<SharedAccount[]> {
  const response = await fetchWithAuth(`${getApiOrigin()}/v1/account/available_accounts`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`available accounts: ${response.status}`);
  const body = (await response.json()) as { accounts?: SharedAccount[] };
  return (body.accounts ?? []).filter((account) => account.services?.includes('mail'));
}

export function withHomeUnreadAccounts(
  sections: ConnectSettingsSection[],
  accounts: readonly SharedAccount[]
): ConnectSettingsSection[] {
  if (accounts.length < 2) return sections;
  return sections.map((section) =>
    section.id !== HOME_SECTION_ID
      ? section
      : {
          ...section,
          fields: [
            ...section.fields,
            ...accounts.map((account) => ({
              key: SHELL_CONFIG_KEY,
              path: ['home', 'unread', 'mail_accounts', account.uuid],
              control: 'switch' as const,
              default: true,
              advanced: true,
              labelKey: `${REF}label/${account.uuid}`,
              descriptionKey: `${REF}description/${account.uuid}`,
            })),
          ],
        }
  );
}

export function homeUnreadAccountTranslator(
  accounts: readonly SharedAccount[],
  t: Translator['t']
): Translator['t'] {
  return (key, named, plural) => {
    if (!key.startsWith(REF)) return t(key, named, plural);
    const [kind, uuid] = key.slice(REF.length).split('/');
    const found = accounts.find((account) => account.uuid === uuid);
    const account = found?.email || found?.name || uuid;
    return t(`${T}.${kind}`, { account });
  };
}
